import React from 'react';
import { MessageContent } from './MessageContent';
import { Copy, ThumbsUp, ThumbsDown, FileText, Image, Download, User, Bot } from 'lucide-react';
import type { Message } from '../../types/chat';

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  onEdit?: (messageId: string, content: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (content: string) => void;
}

export function ChatMessage({ 
  message, 
  isStreaming = false, 
  onEdit, 
  onLike, 
  onDislike, 
  onCopy 
}: ChatMessageProps) {
  const isUser = message.sender === 'user';
  const isBot = message.sender === 'bot';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      onCopy?.(message.content);
    } catch (error) {
      console.error('Échec de la copie:', error);
    }
  };

  const handleDownloadAttachment = (attachment: Message['attachment']) => {
    if (!attachment) return;
    
    try {
      const link = document.createElement('a');
      link.href = attachment.content;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Échec du téléchargement:', error);
    }
  };

  const getAttachmentIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (content: string) => {
    try {
      const size = Math.round((content.length * 3) / 4 / 1024); // Approximation base64
      return size < 1024 ? `${size} Ko` : `${Math.round(size / 1024 * 10) / 10} Mo`;
    } catch {
      return '';
    }
  };

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} group`}>
      <div className={`
        max-w-[85%] md:max-w-[70%] flex flex-col space-y-2
        ${isUser ? 'items-end' : 'items-start'}
      `}>
        {/* Avatar et nom (pour bot seulement) */}
        {isBot && (
          <div className="flex items-center space-x-2 px-1">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">Assistant IA</span>
          </div>
        )}

        {/* Bulle de message */}
        <div className={`
          group message-hover rounded-2xl p-4 shadow-sm border transition-all duration-200 hover:shadow-md
          ${isUser 
            ? 'bg-primary text-primary-foreground rounded-br-md' 
            : 'bg-card text-card-foreground rounded-bl-md'
          }
          ${isStreaming && isBot ? 'animate-pulse' : ''}
        `}>
          {/* Contenu du message */}
          <div className={isUser ? 'text-primary-foreground' : 'text-foreground'}>
            <MessageContent 
              content={message.content}
              isStreaming={isStreaming && isBot}
              onEdit={onEdit ? (newContent) => onEdit(message.id, newContent) : undefined}
            />
          </div>

          {/* Pièce jointe */}
          {message.attachment && (
            <div className={`
              mt-3 p-3 rounded-lg border-2 border-dashed border-border/50 bg-muted/30 attachment-preview
              ${isUser ? 'border-primary-foreground/20 bg-primary-foreground/10' : ''}
            `}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getAttachmentIcon(message.attachment.type)}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
                      {message.attachment.name}
                    </p>
                    <p className={`text-xs ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {message.attachment.type} • {formatFileSize(message.attachment.content)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadAttachment(message.attachment)}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${isUser 
                      ? 'hover:bg-primary-foreground/20 text-primary-foreground' 
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                    }
                  `}
                  title="Télécharger"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions et métadonnées */}
        <div className={`
          flex items-center space-x-2 px-2 action-button
          ${isUser ? 'flex-row-reverse' : 'flex-row'}
        `}>
          {/* Timestamp */}
          <span className="text-xs text-muted-foreground">
            {new Date(message.timestamp).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>

          {/* Actions pour les messages du bot */}
          {isBot && !isStreaming && (
            <>
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Copier"
              >
                <Copy className="h-3 w-3" />
              </button>
              <button
                onClick={() => onLike?.(message.id)}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="J'aime"
              >
                <ThumbsUp className="h-3 w-3" />
                {(message.likes ?? 0) > 0 && (
                  <span className="ml-1 text-xs">{message.likes}</span>
                )}
              </button>
              <button
                onClick={() => onDislike?.(message.id)}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Je n'aime pas"
              >
                <ThumbsDown className="h-3 w-3" />
                {(message.dislikes ?? 0) > 0 && (
                  <span className="ml-1 text-xs">{message.dislikes}</span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}